<template>
    <div>
        <div class="row">
            <div class="col">
                <h1>Edit a product</h1>
            </div>

            <div class="col-auto">
                <locale-switcher @change="fetchAgain" />
            </div>
        </div>

        <form class="mt-4" @submit.prevent="saveProduct">
            <div class="form-group">
                <label for="name">
                    <small class="text-muted">PRODUCT NAME</small>
                </label>
                <input type="text" class="form-control" name="name" placeholder="Name" v-model="name" required autofocus
                    :disabled="loading" />
            </div>

            <div class="form-group">
                <label for="name">
                    <small class="text-muted">PRODUCT NUMBER</small>
                </label>
                <input type="text" class="form-control" name="tag" placeholder="Number" v-model="tag" required autofocus
                    :disabled="loading" />
            </div>

            <div class="form-group">
                <label>
                    <small class="text-muted">DESCRIPTION</small>
                </label>
                <quill-editor v-model="description" :disabled="loading" />
            </div>

            <div class="form-group">
                <label>
                    <small class="text-muted">FEATURES</small>
                </label>
                <quill-editor v-model="features" :disabled="loading" />
            </div>

            <div class="form-group">
                <label>
                    <small class="text-muted">USES</small>
                </label>
                <quill-editor v-model="uses" :disabled="loading" />
            </div>

            <div class="form-group">
                <label for="catalog_name">
                    <small class="text-muted">TYPE</small>
                </label>
                <input class="form-control" name="catalog_name" placeholder="Type (petrol, etc.)" v-model="catalog_name"
                    required autofocus :disabled="loading" />
            </div>
            <div class="form-group">
                <label for="price">
                    <small class="text-muted">PRODUCT PRICE</small>
                </label>
                <input type="number" class="form-control" name="price" placeholder="Price" v-model.number="price" step="0.01" required
                    autofocus :disabled="loading" />
            </div>

            <div class="form-group">
                <label for="width">
                    <small class="text-muted">VOLUME</small>
                </label>
                <input type="text" class="form-control" name="unit" placeholder="Product volume (ex: 325ml)"
                    v-model="unit" required autofocus :disabled="loading" />
            </div>
            <div class="form-group">
                <label for="manufacturer">
                    <small class="text-muted">MANUFACTURER</small>
                </label>
                <select required class="form-control" name="parent" v-model="manufacturer" :disabled="loading">
                    <option value="Proficar">Profi-car</option>
                    <option value="Wynns">Wynn's</option>
                </select>
            </div>

            <div class="form-group">
                <label for="parent">
                    <small class="text-muted">CATEGORY</small>
                </label>
                <select required class="form-control" name="parent" v-model="category_id" :disabled="loading">
                    <option v-for="category in categories" :key="category.id" :value="category.id">{{ category.name }}
                    </option>
                </select>
            </div>

            <div class="form-group">
                <label for="content">
                    <small class="text-muted">PRODUCT MAIN IMAGE</small>
                </label>
                <file-input @change="setMainImage" placeholder="Choose an image" :disabled="loading" />
            </div>
            <div class="form-group">
                <label for="content">
                    <small class="text-muted">PRODUCT IMAGES (OPTIONAL)</small>
                </label>
                <div class="d-flex mb-2">
                    <file-input @change="setImage" placeholder="Choose images (replace old if set)" multiple
                        :disabled="loading" />
                </div>
            </div>
            <button type="submit" class="btn btn-primary ld-ext-right mt-3" :disabled="loading"
                :class="{running: loading}">
                {{ loading ? 'Saving…' : 'Save' }}
                <div class="ld ld-ring ld-spin" />
            </button>
        </form>

        <div class="alert alert-danger mt-4" v-if="!loading && error">
            <p class="mb-0 text-center">{{ error }}</p>
        </div>
    </div>
</template>

<script>
import store from "../../../store";
import { getError } from "../../../helpers";
import { mapGetters, mapState } from "vuex";

import LocaleSwitcher from "../../LocaleSwitcher";
import FileInput from "../../FileInput";

export default {
    components: {
        "file-input": FileInput,
        "locale-switcher": LocaleSwitcher
    },

    async beforeRouteEnter(to, from, next) {
        await store.dispatch("categories/fetchAll");

        return store
            .dispatch("products/fetch", { id: to.params.id })
            .then(product => {
                next(vm => {
                    vm.name = product.name;
                    vm.description = product.description;
                    vm.features = product.features;
                    vm.uses = product.uses;
                    vm.catalog_name = product.catalog_name;
                    vm.tag = product.tag;
                    vm.category_id = product.category_id;
                    vm.unit = product.unit;
                    vm.price = product.price;
                    vm.manufacturer = product.manufacturer;
                });
            });
    },

    data() {
        return {
            name: null,
            catalog_name: null,
            description: null,
            features: null,
            uses: null,
            tag: null,
            manufacturer: "",
            category_id: null,
            unit: null,
            price: null,
            main_image: null,
            images: [],
            newImages: [],

            loading: false,
            error: ""
        };
    },

    computed: {
        ...mapGetters({
            getProductById: "products/getById"
        }),

        product() {
            return this.getProductById(parseInt(this.$route.params.id));
        },

        ...mapState({
            categories: state => state.categories.categories
        })
    },

    methods: {
        saveProduct() {
            this.loading = true;

            return this.$store
                .dispatch("products/update", {
                    id: this.product.id,
                    name: this.name,
                    description: this.description,
                    features: this.features,
                    uses: this.uses,
                    catalog_name: this.catalog_name,
                    tag: this.tag,
                    categoryId: this.category_id,
                    unit: this.unit,
                    price: this.price,
                    main_image: this.main_image,
                    images: this.newImages,
                    manufacturer: this.manufacturer
                })
                .then(() => {
                    this.$router.push({ name: "products" });
                })
                .catch(e => {
                    this.error = getError(e);
                    this.loading = false;
                });
        },

        fetchAgain({ oldValue }) {
            this.loading = true;

            this.$store
                .dispatch("products/update", {
                    id: this.product.id,
                    name: this.name,
                    description: this.description,
                    features: this.features,
                    uses: this.uses,
                    catalog_name: this.catalog_name,
                    tag: this.tag,
                    categoryId: this.category_id,
                    unit: this.unit,
                    price: this.price,
                    manufacturer: this.manufacturer,
                    main_image: this.main_image,
                    images: this.newImages,
                })
                .then(async () => {
                    const product = await this.$store.dispatch(
                        "products/fetch",
                        {
                            id: this.$route.params.id
                        }
                    );

                    this.name = product.name;
                    this.description = product.description;
                    this.features = product.features;
                    this.uses = product.uses;
                    this.catalog_name = product.catalog_name;
                    this.tag = product.tag;
                    this.category_id = product.category_id;
                    this.unit = product.unit;
                    this.price = product.price;
                    this.manufacturer = product.manufacturer;
                })
                .catch(e => {
                    this.$store.dispatch("setContentLocale", oldValue);
                    this.error = getError(e);
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        setMainImage(files) {
            this.main_image = files[0];
        },
        setImage(files) {
            for (let element of files) this.newImages.push(element);
        },

        setPatterendImage(files) {
            this.variants[0].image = files[0];
        },
        setPlainImage(files) {
            this.variants[1].image = files[0];
        }
    },

    meta: {
        title: "Edit a product",

        meta: [
            { property: "og:title", content: "Edit a product / Al-Shayah" }
        ]
    }
};
</script>
